import { CatalystInsight } from '@api/api/catalyst-insights';
import { EarningsInsight } from '@api/api/earnings-insights';
import { Entity } from '@api/api/entity-schema';
import { mapEntity } from '@api/mappers/mapEntity';
import {
  type AppTableFeatures,
  WithNAFallback,
} from '@design/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { OneDayReturnCell } from '@terminal/components/one-day-return-cell/OneDayReturnCell';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CatalystsCell } from '../../../overview/top-companies-table/cells/CatalystsCell';
import { ExposureDirectionCellContent } from './ExposureDirection';
import styles from './TopCompaniesTable.module.scss';

export interface ThemeCompanyWithInsights {
  rank: number;
  exposure_direction?: 'negative' | 'neutral' | 'positive';
  entity: Entity;
  insights: CatalystInsight[];
  earningsInsight?: EarningsInsight;
}

export type TopCompaniesColumnMeta = {
  alignment?: 'start' | 'center' | 'end';
  className?: string;
};

type TopCompaniesColumnDef = ColumnDef<
  AppTableFeatures,
  ThemeCompanyWithInsights
>;

const columnMeta = (
  meta: TopCompaniesColumnMeta
): TopCompaniesColumnDef['meta'] => meta as TopCompaniesColumnDef['meta'];

export const useTopCompaniesTableColumns = (
  isMacroTheme: boolean,
  isCompact = false
): ColumnDef<AppTableFeatures, ThemeCompanyWithInsights>[] => {
  const { t } = useTranslation(['widget']);
  const isBasicUser = useSubscription(state => state.isBasicUser());

  return useMemo<TopCompaniesColumnDef[]>(() => {
    const rankColumn: TopCompaniesColumnDef = {
      id: 'rank',
      header: isCompact
        ? t('widget:themeOverview.tabs.companies.rank')
        : t('widget:themeOverview.tabs.overview.rank'),
      meta: columnMeta({ className: styles.rankColumn }),
      cell: props => (
        <div className={styles.rankFlexRow}>
          <TopRankIcon rank={props.row.original.rank} />
        </div>
      ),
    };

    const companyColumn: TopCompaniesColumnDef = {
      id: 'company',
      header: isCompact
        ? t('widget:themeOverview.tabs.companies.company')
        : t('widget:themeOverview.tabs.overview.revampCompany'),
      meta: columnMeta({ className: styles.companyColumn }),
      cell: props => <EntityCell entityTag={props.row.original.entity.tag} />,
    };

    const oneDayReturnColumn: TopCompaniesColumnDef = {
      id: 'oneDayReturn',
      header: isCompact
        ? t('widget:themeOverview.tabs.companies.return1D')
        : t('widget:themeOverview.tabs.overview.oneDayReturn'),
      meta: columnMeta({
        alignment: isCompact ? undefined : 'end',
        className: styles.oneDayReturnColumn,
      }),
      cell: props => (
        <OneDayReturnCell
          asset={mapEntity(props.row.original.entity)}
          showPercentageSuffix
        />
      ),
    };

    const exposureDirectionColumn: TopCompaniesColumnDef = {
      id: 'exposureDirection',
      header: isCompact
        ? t('widget:themeOverview.tabs.companies.exposureDirection')
        : t('widget:themeOverview.tabs.overview.topCompanies.columnTitle'),
      meta: columnMeta({ className: styles.exposureColumn }),
      cell: props => (
        <ExposureDirectionCellContent
          exposureDirection={props.row.original.exposure_direction}
        />
      ),
    };

    if (isCompact) {
      return [
        rankColumn,
        companyColumn,
        oneDayReturnColumn,
        exposureDirectionColumn,
        {
          id: 'sector',
          header: t('widget:themeOverview.tabs.companies.sector'),
          meta: columnMeta({ className: styles.sectorColumn }),
          cell: props => {
            const sectorCode = props.row.original.entity.gics?.sector.code;

            return (
              <WithNAFallback
                check={!!sectorCode}
                value={t([
                  `widget:assetOverview.companyProfile.sectorNames.${sectorCode}`,
                  'widget:assetOverview.companyProfile.sectorNames.notFound',
                ])}
              />
            );
          },
        },
      ];
    }

    const columns: TopCompaniesColumnDef[] = [
      rankColumn,
      companyColumn,
      ...(isMacroTheme ? [exposureDirectionColumn] : []),
      oneDayReturnColumn,
    ];

    if (!isBasicUser) {
      columns.push({
        id: 'catalystInsights',
        header: t('widget:themeOverview.tabs.overview.revampCatalystInsights'),
        meta: columnMeta({ className: styles.catalystColumn }),
        cell: props => (
          <CatalystsCell
            variant="themeOverview"
            entityTag={props.row.original.entity?.tag}
            insights={props.row.original.insights}
            earningsInsight={props.row.original.earningsInsight}
            source={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW}
          />
        ),
      });
    }

    return columns;
  }, [t, isBasicUser, isMacroTheme, isCompact]);
};
